import { useState } from 'react';
import { Box, Typography, Paper, MenuItem, Select, FormControl, FormHelperText, Chip, alpha } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import chatEvents from '../../lib/chatEvents';

/**
 * Single-screen configuration form: labels on the left, choice selectors on
 * the right, one Submit button. On submit it dispatches a `question_answer`
 * event on the chatEvents bus carrying all chosen values as one text block —
 * the SAME channel QuestionCard uses, so no new listener is required.
 *
 * Props (match the `form_card` SSE part):
 *   title       : string
 *   description : string (optional)
 *   fields      : [{ key, label, options:[{label, value, description?}], default?, required?, description? }]
 *   submitLabel : string
 *   formId      : string
 *   disabled    : boolean (history / already-submitted view)
 */
export default function ConfiguratorForm({
  title,
  description,
  fields = [],
  submitLabel = 'Submit',
  formId,
  disabled = false,
}) {
  // Initialize each field from its default (or first option if none)
  const [values, setValues] = useState(() => {
    const init = {};
    for (const f of fields) {
      const dv = f.default ?? '';
      init[f.key] = dv !== '' ? dv : '';
    }
    return init;
  });
  const [submitted, setSubmitted] = useState(false);
  const isDone = disabled || submitted;

  const missingRequired = fields.filter((f) => f.required && !values[f.key]);

  const handleChange = (key, value) => {
    if (isDone) return;
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (isDone || missingRequired.length > 0) return;
    const lines = fields.map((f) => `- ${f.label}: ${values[f.key] || '—'}`);
    const text = `Configuration:\n${lines.join('\n')}`;
    setSubmitted(true);
    chatEvents.dispatchEvent(
      new CustomEvent('question_answer', { detail: { text, formId } })
    );
  };

  const canSubmit = !isDone && missingRequired.length === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        my: 1.5,
        p: 2.25,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 3,
        overflow: 'hidden',
        background: (t) =>
          t.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${alpha(t.palette.background.paper, 0.6)} 0%, ${alpha(t.palette.background.paper, 0.2)} 100%)`
            : `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.025)} 0%, ${alpha(t.palette.background.paper, 0.9)} 100%)`,
      }}
    >
      {/* Header */}
      {(title || description) && (
        <Box sx={{ mb: 2 }}>
          {title && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 0.6,
                  borderRadius: 1.5,
                  color: 'primary.main',
                  background: (t) => alpha(t.palette.primary.main, 0.12),
                }}
              >
                <TuneRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {title}
              </Typography>
            </Box>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {description}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {fields.map((f, idx) => {
          const val = values[f.key] ?? '';
          const invalid = f.required && !val;
          return (
            <Box
              key={`${formId}-${f.key}`}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 0.5, sm: 1.5 },
                px: 1.75,
                py: 1.25,
                borderTop: idx === 0 ? 'none' : (t) => `1px solid ${t.palette.divider}`,
                background: (t) =>
                  idx % 2 === 0
                    ? 'transparent'
                    : alpha(t.palette.text.primary, t.palette.mode === 'dark' ? 0.02 : 0.012),
                transition: 'background 0.15s ease',
                '&:hover': {
                  background: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.06 : 0.03),
                },
              }}
            >
              {/* Label on the left */}
              <Box sx={{ width: { xs: '100%', sm: '44%' }, minWidth: { sm: 170 }, flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                  {f.label}
                  {f.required && (
                    <Box component="span" sx={{ color: 'error.main', ml: 0.4, fontWeight: 700 }}>
                      *
                    </Box>
                  )}
                </Typography>
                {f.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.4, mt: 0.25 }}
                  >
                    {f.description}
                  </Typography>
                )}
              </Box>

              {/* Choices on the right */}
              <FormControl
                fullWidth
                size="small"
                disabled={isDone}
                error={invalid}
                sx={{ flex: 1 }}
              >
                <Select
                  value={val}
                  displayEmpty
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: 2,
                        boxShadow: (t) => `0 8px 30px ${alpha(t.palette.common.black, 0.18)}`,
                        '& .MuiMenuItem-root': {
                          borderRadius: 1.5,
                          mx: 0.5,
                          my: 0.25,
                          alignItems: 'flex-start',
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: 2,
                    background: (t) => t.palette.background.paper,
                    transition: 'box-shadow 0.18s ease',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (t) => t.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (t) => alpha(t.palette.primary.main, 0.5),
                    },
                    '&.Mui-focused': {
                      boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.15)}`,
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) return <Box component="em" sx={{ opacity: 0.5 }}>Seleziona…</Box>;
                    const opt = f.options.find((o) => o.value === selected);
                    return opt ? opt.label : selected;
                  }}
                >
                  {!f.default && (
                    <MenuItem value="" disabled>
                      <em>Seleziona…</em>
                    </MenuItem>
                  )}
                  {f.options.map((opt, i) => (
                    <MenuItem key={`${formId}-${f.key}-${i}`} value={opt.value}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                          {opt.label}
                        </Typography>
                        {opt.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                            {opt.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {invalid && <FormHelperText sx={{ mx: 0.5 }}>Obbligatorio</FormHelperText>}
              </FormControl>
            </Box>
          );
        })}
      </Box>

      {!isDone && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.25 }}>
          {missingRequired.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {missingRequired.length === 1
                ? '1 campo obbligatorio mancante'
                : `${missingRequired.length} campi obbligatori mancanti`}
            </Typography>
          )}
          <Box
            component="button"
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2.25,
              py: 0.95,
              border: 'none',
              borderRadius: 2,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontSize: '0.875rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              color: (t) => (canSubmit ? t.palette.primary.contrastText : t.palette.text.disabled),
              background: (t) =>
                canSubmit ? t.palette.primary.main : t.palette.action.disabledBackground,
              transition: 'all 0.18s ease',
              '&:hover': canSubmit
                ? {
                    background: (t) => t.palette.primary.dark,
                    boxShadow: (t) => `0 4px 16px ${alpha(t.palette.primary.main, 0.35)}`,
                    transform: 'translateY(-1px)',
                  }
                : {},
              '&:active': canSubmit ? { transform: 'translateY(0)' } : {},
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 18 }} />
            {submitLabel}
          </Box>
        </Box>
      )}

      {isDone && (
        <Box sx={{ mt: 1.75 }}>
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<CheckCircleRoundedIcon />}
            label="Inviato"
            sx={{ fontWeight: 600, borderRadius: 1.5 }}
          />
        </Box>
      )}
    </Paper>
  );
}
